import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { PortalPageHeader } from "@/components/portal-page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import { getEnumMemberName } from "@/lib/enum-utils";
import {
  adminInvitationGetInvitations,
  getAdminInvitationGetInvitationsQueryKey,
  getAdminInvitationGetInvitationsQueryOptions,
  useAdminInvitationResendInvitation,
  useAdminInvitationRevokeInvitation,
} from "@/orval/admin-invitation";
import type { OrganizationInvitationDTO } from "@/orval/model";
import { InvitationStatus, OrganizationRoleType } from "@/orval/model";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { parseISO } from "date-fns/parseISO";
import { MailPlusIcon, RefreshCwIcon, XIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { InviteTeacherDialog } from "./-components/invite-teacher-dialog";
import type { ColumnDef } from "@tanstack/react-table";

const getStatusVariant = (status: number) => {
  if (status === InvitationStatus.Pending) {
    return "default" as const;
  }

  if (status === InvitationStatus.Accepted) {
    return "secondary" as const;
  }

  if (status === InvitationStatus.Expired) {
    return "outline" as const;
  }

  return "destructive" as const;
};

const getStatusLabel = (status: number) => {
  if (status === InvitationStatus.Pending) {
    return "Pending";
  }

  if (status === InvitationStatus.Accepted) {
    return "Accepted";
  }

  if (status === InvitationStatus.Revoked) {
    return "Revoked";
  }

  return "Expired";
};

const OrganizationInvitationsPage: React.FC = () => {
  const { organizationId } = Route.useParams();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const { mutateAsync: resendInvitation, isPending: isResendingInvitation } = useAdminInvitationResendInvitation();
  const { mutateAsync: revokeInvitation, isPending: isRevokingInvitation } = useAdminInvitationRevokeInvitation();

  const handleResendInvitation = async (invitation: OrganizationInvitationDTO) => {
    await resendInvitation({ organizationId, invitationId: invitation.id });
    toast.success("Invitation resent.");
  };

  const handleRevokeInvitation = async (invitation: OrganizationInvitationDTO) => {
    await revokeInvitation({ organizationId, invitationId: invitation.id });
    toast.success("Invitation revoked.");
  };

  const columns = useMemo<ColumnDef<OrganizationInvitationDTO>[]>(
    () => [
      {
        accessorKey: "email",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
        enableSorting: true,
      },
      {
        accessorKey: "targetRoleType",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Role" />,
        enableSorting: true,
        cell: ({ row }) => getEnumMemberName(OrganizationRoleType, row.original.targetRoleType),
      },
      {
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        enableSorting: true,
        cell: ({ row }) => (
          <Badge variant={getStatusVariant(row.original.status)}>{getStatusLabel(row.original.status)}</Badge>
        ),
      },
      {
        accessorKey: "expiresAtUtc",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Expires" />,
        enableSorting: true,
        cell: ({ row }) => format(parseISO(row.original.expiresAtUtc), "PPpp"),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const invitation = row.original;

          if (invitation.status !== InvitationStatus.Pending) {
            return null;
          }

          return (
            <div className="inline-flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleResendInvitation(invitation)}
                disabled={isResendingInvitation || isRevokingInvitation}
              >
                <RefreshCwIcon className="mr-2 size-4" />
                Resend
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleRevokeInvitation(invitation)}
                disabled={isResendingInvitation || isRevokingInvitation}
              >
                <XIcon className="mr-2 size-4" />
                Revoke
              </Button>
            </div>
          );
        },
      },
    ],
    [handleResendInvitation, handleRevokeInvitation, isResendingInvitation, isRevokingInvitation],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Invitations"
        actions={
          <Button onClick={() => setIsInviteDialogOpen(true)}>
            <MailPlusIcon className="mr-2 size-4" />
            Invite Teacher
          </Button>
        }
      />

      <DataTable
        columns={columns}
        queryFn={(queryOptions) => adminInvitationGetInvitations(organizationId, queryOptions)}
        queryKeyFn={(queryOptions) => getAdminInvitationGetInvitationsQueryKey(organizationId, queryOptions)}
        getRowIdFn={(row) => row.id.toString()}
      />

      <InviteTeacherDialog
        organizationId={organizationId}
        isOpen={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/invitations")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getAdminInvitationGetInvitationsQueryOptions(params.organizationId, buildDataTableQueryOptions({ searchString })),
    );
  },
  component: OrganizationInvitationsPage,
});
